import { useFilterContext as useGlobalFilterContext, useOptionalFilterContext } from '../contexts/FilterContext';
import { useCallback, useMemo, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import moment from 'moment';

const SALES_DATE_QUERY_KEY = 'salesDate';

// Re-export the global filter context hook
export const useFilterContext = useGlobalFilterContext;

// Simple hook for components that just need the current filter values
export const useFilterValues = () => {
  const { filters } = useGlobalFilterContext();
  return {
    startDate: filters.startDate,
    endDate: filters.endDate,
    count: filters.count,
  };
};

export const useOptionalFilterValues = () => {
  const context = useOptionalFilterContext();

  return {
    startDate: context?.filters.startDate ?? null,
    endDate: context?.filters.endDate ?? null,
    count: context?.filters.count ?? null,
  };
};

// Hook for components that need filter actions
export const useFilterActions = () => {
  const { setDateRange, resetDateRange, clearAllFilters } = useGlobalFilterContext();
  return {
    setDateRange,
    resetDateRange,
    clearAllFilters,
  };
};

// Hook for components that need both values and actions
export const useFilterState = () => {
  const { filters, setDateRange, resetDateRange, clearAllFilters, getFiltersForAPI } = useGlobalFilterContext();
  return {
    filters,
    setDateRange,
    resetDateRange,
    clearAllFilters,
    getFiltersForAPI,
  };
};

// Hook to check if date filters are active (non-default)
export const useActiveFilters = () => {
  const { filters } = useGlobalFilterContext();
  const { getDefaultDateRange, formatDateWithTimezone, calculateDays } = require('../contexts/FilterContext');
  
  const defaultRange = getDefaultDateRange();
  const defaultStartDate = formatDateWithTimezone(defaultRange.startDate, true);
  const defaultEndDate = formatDateWithTimezone(defaultRange.endDate, false);
  const defaultCount = calculateDays(defaultRange.startDate, defaultRange.endDate).toString();
  
  const hasActiveFilters = !!(
    filters.startDate !== defaultStartDate ||
    filters.endDate !== defaultEndDate ||
    filters.count !== defaultCount
  );
  
  return hasActiveFilters;
};

// Enhanced hook for date filter management with URL persistence
export const useDateFilter = () => {
  const { filters, setDateRange, resetDateRange, clearAllFilters, getFiltersForAPI } = useGlobalFilterContext();
  const { 
    hasDateParamsInURL, 
    getDateParamsFromURL, 
    setDateParamsInURL, 
    clearDateParamsFromURL,
    validateDateParams,
    getDateRangeFromStaticRange,
    ensureDateParamsInURL,
    syncDateParamsToURL,
    hasSameDateParamsInURL
  } = require('../utils');

  // Enhanced setDateRange that also updates URL
  const setDateRangeWithURL = useCallback((startDate: any, endDate: any) => {
    setDateRange(startDate, endDate);
    
    // URL updates are handled by FilterContext with replace: true option
    // No need to manually call syncDateParamsToURL here
  }, [setDateRange]);

  // Enhanced resetDateRange that also clears URL
  const resetDateRangeWithURL = useCallback(() => {
    resetDateRange();
    // URL clearing is handled by FilterContext with replace: true option
  }, [resetDateRange]);

  // Enhanced clearAllFilters that also clears URL
  const clearAllFiltersWithURL = useCallback(() => {
    clearAllFilters();
    // URL clearing is handled by FilterContext with replace: true option
  }, [clearAllFilters]);

  // Function to ensure date params are always in URL (for route changes)
  const ensureDateParamsInCurrentURL = useCallback(() => {
    if (filters.startDate && filters.endDate) {
      ensureDateParamsInURL(filters.startDate, filters.endDate, filters.count || undefined);
    }
  }, [filters.startDate, filters.endDate, filters.count]);

  // Check if current filters match URL params
  const isURLSynced = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return false;
    return hasSameDateParamsInURL(filters.startDate, filters.endDate, filters.count || undefined);
  }, [filters, hasSameDateParamsInURL]);

  // Get formatted date range for display
  const formattedDateRange = useMemo(() => {
    if (!filters.startDate || !filters.endDate) {
      return 'Select Date Range';
    }
    const { formatDateRangeForDisplay } = require('../utils');
    return formatDateRangeForDisplay(filters.startDate, filters.endDate);
  }, [filters.startDate, filters.endDate]);

  return {
    // Current filter values
    filters,
    startDate: filters.startDate,
    endDate: filters.endDate,
    count: filters.count,
    
    // Actions
    setDateRange: setDateRangeWithURL,
    resetDateRange: resetDateRangeWithURL,
    clearAllFilters: clearAllFiltersWithURL,
    getFiltersForAPI,
    
    // Utility functions
    hasDateParamsInURL,
    getDateParamsFromURL,
    setDateParamsInURL,
    clearDateParamsFromURL,
    validateDateParams,
    getDateRangeFromStaticRange,
    ensureDateParamsInURL: ensureDateParamsInCurrentURL,
    syncDateParamsToURL,
    hasSameDateParamsInURL,
    
    // Computed values
    isURLSynced,
    formattedDateRange,
    hasActiveFilters: useActiveFilters(),
  };
};

// Hook specifically for route components to ensure date params are always in URL
export const useRouteDateFilter = () => {
  const { filters } = useDateFilter();
  const { useEffect } = require('react');

  // URL updates are handled by FilterContext with replace: true option
  // No need to manually call ensureDateParamsInURL here
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      console.log('RouteDateFilter - Date params are handled by FilterContext for route:', window.location.pathname);
    }
  }, [filters.startDate, filters.endDate]);

  return {
    filters,
    startDate: filters.startDate,
    endDate: filters.endDate,
    count: filters.count,
    ensureDateParamsInURL: () => {
      // This function is kept for backward compatibility but does nothing
      // since URL updates are handled by FilterContext
      console.log('RouteDateFilter - ensureDateParamsInURL called but URL updates are handled by FilterContext');
    },
  };
};

// Hook for single date management (for summary route)
export const useSingleDate = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Check if we're on the summary route
  const isSalesRoute = location.pathname.includes('/sales');

  // Get current single date from URL (recomputes when URL param changes)
  const salesDateParam = searchParams.get(SALES_DATE_QUERY_KEY);
  const dateParam = salesDateParam;
  const singleDate = useMemo(() => {
    if (dateParam) {
      const parsed = moment(dateParam, 'YYYY-MM-DD');
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }
    // Default to today in the browser's local timezone if no date in URL
    return moment().startOf('day').toDate();
  }, [dateParam]); // Only depend on dateParam to avoid unnecessary re-renders

  // Handler for single date change (updates URL using React Router)
  const handleSingleDateChange = useCallback((date: Date | null) => {
    if (isSalesRoute && date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      const newParams = new URLSearchParams(searchParams);
      newParams.set(SALES_DATE_QUERY_KEY, formattedDate);
      setSearchParams(newParams, { replace: true });
    }
  }, [isSalesRoute, searchParams, setSearchParams]);

  // Initialize date param if missing (for summary route)
  useEffect(() => {
    if (!isSalesRoute) {
      return;
    }

    const newParams = new URLSearchParams(searchParams);

    if (salesDateParam) {
      return;
    }

    const sourceDate = moment().format('YYYY-MM-DD');
    newParams.set(SALES_DATE_QUERY_KEY, sourceDate);
    setSearchParams(newParams, { replace: true });
  }, [isSalesRoute, salesDateParam, searchParams, setSearchParams]);

  return {
    singleDate,
    handleSingleDateChange,
    isSalesRoute,
    hasSingleDateInURL: !!dateParam,
  };
};

// Re-export utility functions
export {
  getDefaultDateRange,
  formatDateWithTimezone,
  calculateDays,
  defaultFilters,
  initializeFilters,
  setDateRange,
  resetDateRange,
  clearAllFilters,
  getFiltersForAPI,
} from '../contexts/FilterContext'; 
